import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  mapOrderLimitAdjustmentToInternal,
  MONTHLY_ORDER_LIMIT,
  OrderLimitAdjustment,
  OrderLimitAdjustmentDto,
  OrderLimitAdjustmentItem,
  OrderLimitStatus,
  ProductCart,
  ProductDetailsCart,
} from '@models';
import { Observable, of } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';

import { isAppleBrand } from '../utils/product.utils';
import { ApiService } from './api-service';
import { CartDataService } from './cart-data.service';

/**
 * A merch banner-worthy outcome (kept as data, not rendered copy — @core stays free of the strings
 * that live in @shared). Components compose the text from `ORDER_LIMIT_MESSAGES`.
 */
export interface OrderLimitBannerEvent {
  type: 'adjusted' | 'removed';
  /** RID-1356 — the SKU the event describes, so the cart can hang the message on its own tile. */
  productId: number;
  productName: string;
  /** Quantity already purchased this month (feeds "You previously ordered [##]"). */
  alreadyPurchased: number;
}

/** AC07b — one consolidated Apple removal (shown above the Apple group in the cart). */
export interface OrderLimitAppleRemoval {
  productId: number;
  productName: string;
  removedCount: number;
  alreadyPurchased: number;
}

/**
 * RID-1292 — outcome of the checkout-time gate. `adjusted` is false when the backend blocked
 * checkout but took nothing away (a cart that exactly fits the remaining allowance, or a repeat
 * normalize on an already-normalized cart) — the caller must then continue to Checkout.
 */
export interface OrderLimitCheckoutOutcome {
  adjusted: boolean;
  /** The cart as re-read after the gate — the only trustworthy snapshot for the next step. */
  cart: ProductDetailsCart;
}

/**
 * FCR 19.1 Suspected Misuse — single point of the monthly order-limit contract + interpretation.
 *
 * The backend owns the enforcement: `POST cart/normalize` evaluates the cart against the monthly
 * per-SKU limit AND APPLIES the outcome — it reduces/removes the lines server-side. So it is called
 * from the "Go to Checkout" action only (AC06/AC07) and never from a read path. RID-1279: firing it
 * on cart load / mini-cart open silently deleted items before checkout, which AC03 forbids. The
 * frontend itself never mutates the cart.
 *
 * Until checkout has run, the display caps come from cart state alone — "Add to Cart" (AC01) and the
 * cart stepper both cap against `MONTHLY_ORDER_LIMIT`, with no purchase history involved (AC03/AC05).
 * Once the checkout adjustment has answered, the per-SKU picture caps the stepper at the remaining
 * allowance (RID-1278) and drives the reduce/remove banners.
 *
 * AC12 / RID-1360 — that display state must NOT survive leaving and reopening the cart: a plain
 * cart open shows no banner, no per-tile message and no history-based stepper cap, exactly like a
 * cart that never hit the limit (AC03). Root scope keeps the signals alive for the whole tab, so
 * the cart page clears them through `reset()` when it is destroyed; a reload empties them anyway.
 * Nothing is rebuilt on cart open — RID-1315 used to re-derive the state from a normalize call
 * there, which both fired the mutating endpoint on a read path and guessed the adjustment from
 * `alreadyPurchased` alone. Within a single visit the state is still keyed to a fingerprint of the
 * cart it describes, so any change the customer makes drops it.
 */
@Injectable({ providedIn: 'root' })
export class OrderLimitService {
  private readonly apiService = inject(ApiService);
  private readonly cartDataService = inject(CartDataService);
  private readonly destroyRef = inject(DestroyRef);

  /** BE order-limit endpoint — evaluates AND applies the adjustment (checkout action only). */
  private readonly endpoint = 'cart/normalize';

  /** Per-SKU picture from the last checkout-time normalize (empty until checkout runs). */
  private readonly report = signal<OrderLimitAdjustmentItem[]>([]);

  /** Merch banner events for the cart top-warning stack, for the current cart visit (AC13). */
  readonly bannerEvents = signal<OrderLimitBannerEvent[]>([]);

  /** Per-product "previously ordered" state for auto-reduced Merch tiles (AC07a). */
  private readonly reducedProducts = signal<Map<number, number>>(new Map());

  /** Consolidated Apple removals shown above the Apple group (AC07b). */
  readonly appleRemovals = signal<OrderLimitAppleRemoval[]>([]);

  /**
   * Fingerprint of the cart the current display state describes; `null` while there is no state.
   * Any cart that no longer matches it invalidates the state.
   */
  private stateCartSignature: string | null = null;

  constructor() {
    // No normalize on cart changes — it applies the adjustment server-side (AC03/RID-1279). What is
    // watched here is the cart drifting away from the one the state was built for: any quantity
    // change, removal or addition invalidates it. A `null` cart means "not loaded yet", not "empty",
    // so it is ignored — otherwise the initial emission on bootstrap would wipe the state the
    // checkout gate just built. Keying on the fingerprint also covers RID-1286, where the removal
    // itself empties the cart: that empty cart is the one the state was built for, so its message
    // survives.
    toObservable(this.cartDataService.productDetailsCart)
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe((cart) => {
        if (!cart || this.stateCartSignature === null) return;
        if (this.cartSignature(cart) === this.stateCartSignature) return;
        this.reset();
      });
  }

  /**
   * Identity of a cart for the purposes of the limit validation: which SKUs, on which lines, in
   * which quantities. Anything the customer can change in the cart changes this.
   */
  private cartSignature(cart: ProductDetailsCart): string {
    return (cart.products ?? [])
      .map((product) => `${product.productId}:${product.lineNumber ?? ''}:${product.quantity ?? 0}`)
      .sort()
      .join('|');
  }

  private fetchNormalization(): Observable<OrderLimitAdjustment> {
    return (this.apiService.post(this.endpoint, {}) as Observable<OrderLimitAdjustmentDto>).pipe(
      map(mapOrderLimitAdjustmentToInternal),
      tap((adjustment) => this.report.set(adjustment.products)),
    );
  }

  /** AC02 / AC05 / AC03 — cart-only cap: the quantity in the cart reaches the monthly max. */
  isLimitReached(currentQuantity: number): boolean {
    return currentQuantity >= MONTHLY_ORDER_LIMIT;
  }

  /** The normalize picture for a SKU — undefined until normalize has answered for it. */
  private reportFor(productId: number): OrderLimitAdjustmentItem | undefined {
    return this.report().find((item) => item.productId === productId);
  }

  /**
   * RID-1278 — remaining monthly allowance for a SKU: `maxAllowed - alreadyPurchased`
   * (`maxAllowed` is the SKU's full monthly limit, not the remainder — the backend compares
   * `alreadyPurchased + cartQuantity` against it). Falls back to `MONTHLY_ORDER_LIMIT` while the
   * picture is missing, so the cap never blocks the cart before normalize answers.
   */
  remainingFor(productId: number): number {
    const item = this.reportFor(productId);
    if (!item) return MONTHLY_ORDER_LIMIT;
    return Math.max(0, item.maxAllowed - item.alreadyPurchased);
  }

  /** Quantity already purchased this month for a SKU (0 while the picture is missing). */
  alreadyPurchasedFor(productId: number): number {
    return this.reportFor(productId)?.alreadyPurchased ?? 0;
  }

  /**
   * RID-1278 — cap the cart "+": the line quantity reached the remaining monthly allowance,
   * or the product was auto-reduced at checkout (AC07a — stays capped afterwards).
   */
  isProductLimitReached(productId: number, currentQuantity: number): boolean {
    return currentQuantity >= this.remainingFor(productId) || this.wasReduced(productId);
  }

  /**
   * RID-1284 — AC05: limit state for a cart tile. Merch keeps the whole quantity on one line, so the
   * line quantity is the SKU quantity. Since RCP-379 Apple does too, but carts saved before it can
   * still spread a SKU over one line per unit, so Apple compares the per-SKU total.
   */
  isTileLimitReached(product: ProductCart): boolean {
    const quantity = isAppleBrand(product) ? this.cartQuantityForSku(product.productId) : (product.quantity ?? 0);
    return this.isProductLimitReached(product.productId, quantity);
  }

  /** Quantity of a SKU across all cart lines (pre-RCP-379 Apple carts may hold several). */
  cartQuantityForSku(productId: number | null | undefined): number {
    if (productId == null) return 0;
    return (this.cartDataService.productDetailsCart()?.products ?? [])
      .filter((product) => product.productId === productId)
      .reduce((sum, product) => sum + (product.quantity ?? 0), 0);
  }

  /**
   * AC01 — restrict "Add to Cart" for this SKU based on cart state only (AC03 — no history on display).
   * Sums the quantity across all cart lines sharing the productId; products not in the cart return false.
   */
  isSkuAtLimit(productId: number | null | undefined): boolean {
    const inCartQuantity = this.cartQuantityForSku(productId);
    if (inCartQuantity === 0) return false;
    return this.isLimitReached(inCartQuantity);
  }

  /**
   * AC04 — reads `isValidForCheckout` from `GET cart`. Defaults to allowed when absent. Components go
   * through `resolveCheckoutGate()` instead, so the flag is only ever read off a freshly fetched cart.
   */
  canCheckout(cart: ProductDetailsCart | null): boolean {
    return cart?.isValidForCheckout !== false;
  }

  /** AC07a — the product was auto-reduced (held until the cart changes or the customer leaves). */
  wasReduced(productId: number): boolean {
    return this.reducedProducts().has(productId);
  }

  /**
   * RID-1292 — the checkout gate. `isValidForCheckout` can be stale in the local cart (the
   * optimistic update/remove keeps the old flag and the refetch is debounced), so the cart is
   * re-read before the mutating normalize: the adjustment must never fire for a cart that is
   * already within the limit (AC03/RID-1279).
   */
  resolveCheckoutGate(): Observable<OrderLimitCheckoutOutcome> {
    return this.cartDataService
      .fetchProductCart()
      .pipe(
        switchMap((cart) =>
          this.canCheckout(cart) ? of({ adjusted: false, cart }) : this.runNormalizeAtCheckout(cart),
        ),
      );
  }

  /**
   * AC06 / AC07 — surface the reduce/remove banners for the current picture (built on the "Go to
   * Checkout" action so nothing shows on initial load — AC03), then refetch the cart so the display
   * reflects the backend-managed contents. The frontend applies nothing itself.
   *
   * RID-1292 — reports back whether the backend actually took anything away: a blocked gate alone
   * is not an adjustment, and a no-op normalize must not strand the customer on the cart.
   */
  runNormalizeAtCheckout(cart: ProductDetailsCart): Observable<OrderLimitCheckoutOutcome> {
    return this.fetchNormalization().pipe(
      map(() => this.buildDisplayState(cart)),
      switchMap((adjusted) =>
        this.cartDataService.fetchProductCart().pipe(
          // The state describes the cart as the backend left it, so the fingerprint has to be taken
          // from the refetched cart — the pre-adjustment one would never match again.
          tap((adjustedCart) => {
            if (adjusted) this.stateCartSignature = this.cartSignature(adjustedCart);
          }),
          map((adjustedCart) => ({ adjusted, cart: adjustedCart })),
        ),
      ),
    );
  }

  /** @returns whether this normalize actually reduced/removed anything (RID-1292). */
  private buildDisplayState(cart: ProductDetailsCart): boolean {
    const bannerEvents: OrderLimitBannerEvent[] = [];
    const reduced = new Map<number, number>();
    const appleRemovals: OrderLimitAppleRemoval[] = [];

    for (const item of this.report()) {
      const lines = cart.products.filter((product) => product.productId === item.productId);
      if (!lines.length) continue;
      if (item.status !== OrderLimitStatus.Reduced && item.status !== OrderLimitStatus.Removed) continue;

      const product = lines[0];
      const preQuantity = lines.reduce((sum, line) => sum + (line.quantity ?? 0), 0);
      const removedCount = item.status === OrderLimitStatus.Removed ? preQuantity : preQuantity - item.updatedQuantity;

      // RID-1292 — the backend reports Reduced/Removed even when it took nothing away: a cart that
      // exactly fits the remaining allowance, or a repeat checkout on an already-normalized cart.
      // Nothing changed, so there is nothing to say and nothing to cap.
      if (removedCount <= 0) continue;

      // RCP-452 — since RCP-379 Apple keeps its quantity on one line and is reduced like merch;
      // only a pre-RCP-379 cart spreading the SKU over several lines loses whole tiles.
      if (isAppleBrand(product) && lines.length > 1) {
        // Apple: excess tiles removed by the backend — one consolidated message above the group (AC07b).
        appleRemovals.push({
          productId: item.productId,
          productName: product.name,
          removedCount,
          alreadyPurchased: item.alreadyPurchased,
        });
      } else if (item.status === OrderLimitStatus.Reduced) {
        bannerEvents.push({
          type: 'adjusted',
          productId: item.productId,
          productName: product.name,
          alreadyPurchased: item.alreadyPurchased,
        });
        reduced.set(item.productId, item.alreadyPurchased);
      } else {
        bannerEvents.push({
          type: 'removed',
          productId: item.productId,
          productName: product.name,
          alreadyPurchased: item.alreadyPurchased,
        });
      }
    }

    // A no-op normalize must not wipe the picture an earlier real adjustment left behind (AC06/AC07
    // banners + RID-1278 stepper caps). `report()` is refreshed by `fetchNormalization` either way.
    if (!bannerEvents.length && !appleRemovals.length) return false;

    // RID-1356 — every affected SKU keeps its own event: the cart shows the message above that
    // product's tile, so collapsing them to one per scenario type would silence all but one.
    this.bannerEvents.set(bannerEvents);
    this.reducedProducts.set(reduced);
    this.appleRemovals.set(appleRemovals);
    return true;
  }

  /**
   * Clear all display state — when the cart no longer matches the one it describes, and when the
   * customer leaves the Cart page, so a reopen starts clean (AC12 / RID-1360).
   */
  reset(): void {
    this.report.set([]);
    this.bannerEvents.set([]);
    this.reducedProducts.set(new Map());
    this.appleRemovals.set([]);
    this.stateCartSignature = null;
  }
}
